/**
 * 初始化 Footer Actions 浮動控制（使用 Intersection Observer API）
 *
 * @param {number|object} triggerDistanceConfig - 觸發距離配置
 *
 * @example
 * // 所有裝置統一距離
 * initStickyFooter(80);
 *
 * @example
 * // RWD 配置（推薦）
 * initStickyFooter({ base: 0, lg: 80 });
 * // base: 預設值，lg/md/xl 等: 對應 breakpoint 的值
 *
 * @description
 * 行為說明：
 * - 頁面最上方：隱藏按鈕
 * - 頁面中間：固定在右下角
 * - 接近 footer：絕對定位在 footer 上方
 *
 * triggerDistance 說明：
 * - 0: 在 footer 交界處切換
 * - 正數: 在 footer 上方 X px 處切換
 * - 負數: 進入 footer 下方 X px 處切換
 */
export function initStickyFooter(triggerDistanceConfig) {
  /* ------------------------------- [START] Footer Fix */
  const footer = document.getElementById('footer');
  const footerActions = document.getElementById('footer-actions');

  if (!footer || !footerActions) return;

  const hideClassName = 'is-hide';
  const fixedClassName = 'is-fixed';

  // Bootstrap breakpoints (與 SCSS 同步)
  const BREAKPOINTS = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
  };

  // 觸發距離配置（支援 RWD）
  const defaultConfig = { base: 0 };
  const config =
    typeof triggerDistanceConfig === 'number'
      ? { base: triggerDistanceConfig }
      : { ...defaultConfig, ...triggerDistanceConfig };

  // 根據當前視窗寬度取得對應的 triggerDistance
  function getTriggerDistance() {
    const windowWidth = window.innerWidth;
    let distance = config.base;

    const breakpointKeys = Object.keys(BREAKPOINTS).reverse();
    for (const key of breakpointKeys) {
      if (windowWidth >= BREAKPOINTS[key] && config[key] !== undefined) {
        distance = config[key];
        break;
      }
    }

    return distance;
  }

  // 快取當前觸發距離
  let currentTriggerDistance = getTriggerDistance();

  // 使用 Intersection Observer 監測頁面頂部（用於判斷是否在最上方）
  // sentinel 需要放在一個不會跟著滾動的參考點，這裡用 body 的第一個子元素前面
  let isAtTop = window.scrollY === 0;
  const topSentinel = document.createElement('div');
  // 使用 height: 100px 讓 sentinel 有足夠高度來偵測離開
  topSentinel.style.cssText = 'position: absolute; top: 0; left: 0; width: 1px; height: 100px; pointer-events: none;';
  document.body.prepend(topSentinel);

  const topObserver = new IntersectionObserver(
    ([entry]) => {
      // 當 sentinel 的 top 邊界低於視窗頂部時，表示已經滾動離開頂部
      isAtTop = entry.boundingClientRect.top >= 0;
      updateFooterActionsState();
    },
    { threshold: [0, 0.1, 0.5, 1] }
  );
  topObserver.observe(topSentinel);

  // 使用 Intersection Observer 監測 footer（用於判斷是否接近 footer）
  let isNearFooter = false;
  let footerObserver = null;

  function createFooterObserver() {
    if (footerObserver) footerObserver.disconnect();

    currentTriggerDistance = getTriggerDistance();
    const rootMargin = `0px 0px ${-currentTriggerDistance}px 0px`;

    footerObserver = new IntersectionObserver(
      ([entry]) => {
        isNearFooter = entry.isIntersecting;
        updateFooterActionsState();
      },
      { rootMargin, threshold: [0, 0.01] }
    );

    footerObserver.observe(footer);
  }

  // 更新 footerActions 的狀態
  function updateFooterActionsState() {
    if (isAtTop) {
      footerActions.classList.add(hideClassName, fixedClassName);
    } else {
      footerActions.classList.remove(hideClassName);

      if (isNearFooter) {
        footerActions.classList.remove(fixedClassName);
      } else {
        footerActions.classList.add(fixedClassName);
      }
    }
  }

  // 處理 resize 事件（需要重新建立 observer）
  let resizeTimer = null;
  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      createFooterObserver();
      updateFooterActionsState();
    }, 100);
  }

  // 初始化
  createFooterObserver();
  updateFooterActionsState();

  // 監聽 resize
  window.addEventListener('resize', handleResize);

  // 清理函數（可選，用於需要銷毀時調用）
  return () => {
    topObserver.disconnect();
    if (footerObserver) footerObserver.disconnect();
    window.removeEventListener('resize', handleResize);
    topSentinel.remove();
  };
  /* ------------------------------- [END] Footer Fix */
}
